import { useState } from 'react';

import { Center, Flex, HStack, SimpleGrid, Spinner, Text } from '@chakra-ui/react';

import {
  ActionIconButton,
  ChevronLeftOutline,
  ChevronRightOutline,
  PayCalendar,
} from '@aslanpay/portal-common-lib';

import { PayScheduleCard } from 'app/components/Dashboard/PayScheduleCard';

interface PayScheduleSectionProps {
  payCalendars: PayCalendar[];
  isLoading: boolean;
}

// Show at most a 2x2 grid (4 cards) at a time; page through the rest with the arrows.
const PAGE_SIZE = 4;

function PayScheduleSection({ payCalendars, isLoading }: PayScheduleSectionProps) {
  const [page, setPage] = useState(0);

  const totalPages = Math.ceil(payCalendars.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(0, totalPages - 1));
  const visible = payCalendars.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const showControls = totalPages > 1;

  return (
    <Flex flexDir="column" gap="16px" mt="30px">
      <Flex align="center" justify="space-between" gap="16px">
        <Text as="h5" textStyle="h5SemiBold" color="brand.darkGray">
          Pay schedule
        </Text>

        {showControls && (
          <HStack spacing="8px">
            <ActionIconButton
              aria-label="Previous pay schedules"
              icon={<ChevronLeftOutline boxSize="18px" />}
              variant="outline"
              isDisabled={safePage === 0}
              onClick={() => setPage(safePage - 1)}
            />
            <Text textStyle="smallMedium" color="brand.darkGray">
              {safePage + 1} / {totalPages}
            </Text>
            <ActionIconButton
              aria-label="Next pay schedules"
              icon={<ChevronRightOutline boxSize="18px" />}
              variant="outline"
              isDisabled={safePage >= totalPages - 1}
              onClick={() => setPage(safePage + 1)}
            />
          </HStack>
        )}
      </Flex>

      {isLoading && (
        <Center py="40px">
          <Spinner color="brand.purple" size="lg" />
        </Center>
      )}

      {!isLoading && payCalendars.length === 0 && (
        <Flex py="40px" px="20px" bgColor="brand.lightGray2" borderRadius="8px" justify="center">
          <Text textStyle="body" color="brand.darkGray">
            No active pay calendars for this company.
          </Text>
        </Flex>
      )}

      {!isLoading && payCalendars.length > 0 && (
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="20px">
          {visible.map((payCalendar) => (
            <PayScheduleCard key={payCalendar.id} payCalendar={payCalendar} />
          ))}
        </SimpleGrid>
      )}
    </Flex>
  );
}

export { PayScheduleSection };

export default PayScheduleSection;
